/**
 * Центр отчётов: сборка, выгрузки и публичная ссылка.
 *
 * Два обещания контракта, которые экран обязан держать на виду.
 *
 * 1. **Выгрузка сверяется с экраном.** В отчёт кладутся те самые округлённые
 *    числа, которые клиент видел; при расхождении с пересчитанными отчёт падает
 *    с ошибкой, вместо того чтобы отдать неверный документ. Поэтому состояние
 *    `failed` здесь — не поломка кнопки, а сработавшая защита, и говорится это
 *    словами.
 * 2. **Файл отдаёт хранилище, а не API.** Скачивание — обычная ссылка на
 *    `/reports/{id}/download`, которая уводит редиректом на подписанный адрес,
 *    живущий пятнадцать минут.
 *
 * Публичная ссылка — отдельное право (`owner`, `analyst`): она выносит числа
 * клиента наружу, и роль, которая только смотрит, её не создаёт.
 */

import { useEffect, useRef, useState } from 'react';

import { api, downloadFile, idempotencyKey, requestWith } from '../api/client';
import { ExportFailure, orderAndDownload } from '../api/exports';
import { useAccess } from '../session';
import { useScope } from '../scope';
import { ScreenHead } from '../ui/screen';
import { isoDate } from '@shared/src/intl';

import { useRead, daysAgo, type Page } from '../ui/query';
import { Empty, ErrorNotice, Pipeline, Skeleton } from '../ui/state';
import { Stamp } from '../ui/viz';
import { Confirm } from '../ui/fields';
import {
  countOf,
  dateNumeric,
  dateProse,
  decimal,
  int,
  period as periodFmt,
} from '@shared/src/format';
import { LEGAL_ENTITY } from '@shared/src/product';
import { reportKind } from '../ui/labels';
import type { components } from '../api/schema';
import { pickBrand } from '../ui/screen-states';

type Report = components['schemas']['Report'];
type ReportKind = components['schemas']['ReportKind'];
type ReportFormat = components['schemas']['ReportFormat'];
type ReportBuildFormat = components['schemas']['ReportBuildFormat'];

/**
 * Что можно собрать **с этого экрана**. Не весь `KIND`.
 *
 * `agency` требует `workspace_id`, которого эта форма не собирает, и белой
 * марки на тарифе Масштаб и выше — он живёт на «Клиентах», где пространство
 * известно (`createAgencyReport`). `audit_carryover` заводится присвоением
 * бесплатного аудита, а не рукой владельца, и в списке готовых отчётов
 * подписан. Предлагать действие, которое заведомо упрётся в 402 или 422, —
 * ровно то «показать и упереться в отказ», от которого app-kit.md §1
 * отказывается прямо.
 */
const CREATABLE: ReportKind[] = ['weekly', 'monthly', 'custom'];

/**
 * Названия форматов. Словарь полный: он подписывает **готовые** отчёты в
 * списке, а там встречаются и `pdf`, заказанные до того, как формат перестали
 * предлагать.
 */
const FORMAT: Record<ReportFormat, string> = {
  pdf: 'PDF',
  xlsx: 'XLSX',
  html: 'Страница',
  json: 'Данные (JSON)',
};

/**
 * Что можно **заказать**. Перечисление приходит из контракта
 * (`ReportBuildFormat`), а не из головы: пока список жил во фронте, форма
 * предлагала PDF и XLSX, которых сборка не умела, — заказ вставал в очередь с
 * кодом 202 и через минуту возвращался отказом «формат не поддерживается».
 * Проверено на проде 9 августа 2026: недельный отчёт в PDF, отказ через 43 с.
 *
 * Теперь сборщик кладёт файлом все четыре, и списки сошлись.
 */
const BUILDABLE: ReportBuildFormat[] = ['html', 'pdf', 'xlsx', 'json'];

/**
 * Форматы, которые предлагаются кнопкой прямо на строке готового отчёта.
 *
 * Это два формата, за которыми приходят с готовым числом на руках: PDF — чтобы
 * приложить к письму, XLSX — чтобы считать дальше. Страница и данные остаются в
 * форме заказа: их берут осознанно, а не «дай файл».
 */
const QUICK_EXPORTS: ReportBuildFormat[] = ['pdf', 'xlsx'];

/** Шаги сборки. Счётчика у неё нет, поэтому показываются шаги, а не полоса. */
const BUILD_STEPS = ['Собираем числа', 'Сверяем с экраном', 'Готовим файл'];

/** Как часто спрашивать состояние собирающегося отчёта, если сервер не сказал иначе. */
const POLL_FALLBACK = 4;
/** Дальше интервал не растим: 4 → 8 → 16 секунд. */
const POLL_MAX = 16;
/** Столько неудач подряд — и перестаём молча ждать, говорим человеку. */
const MAX_MISSES = 4;

export function Reports() {
  const access = useAccess();
  const scope = useScope();
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const list = useRead<Page<Report>>('/reports', {
    limit: 30,
    brand_id: scope.brandId ?? undefined,
  });

  const canCreate = access.reaches('POST', '/reports');
  const canShare = access.reaches('POST', '/reports/{reportId}/share');

  const [kind, setKind] = useState<ReportKind>('weekly');
  const [format, setFormat] = useState<ReportBuildFormat>('html');
  const [withAnswers, setWithAnswers] = useState(false);
  // Состояние собирающихся отчётов перестало доходить. Молчать нельзя: карточка
  // так и будет показывать «Собираем числа», а человек — жать «Собрать» второй
  // раз и получать дубль.
  const [lost, setLost] = useState(false);

  // Пока хоть один отчёт собирается, спрашиваем его состояние: потока событий в
  // продукте нет, а «обновите страницу» — не ответ.
  const building =
    list.kind === 'ready'
      ? list.data.items.filter(
          (report) => report.state === 'queued' || report.state === 'building',
        )
      : [];
  const buildingIds = building.map((report) => report.id).join(',');

  useEffect(() => {
    if (!buildingIds) return;
    let timer: number | undefined;
    let misses = 0;
    const abort = new AbortController();

    const tick = () => {
      Promise.all(
        buildingIds
          .split(',')
          .map((id) =>
            requestWith<Report>(`/reports/${id}`, { signal: abort.signal }).catch(
              () => null,
            ),
          ),
      )
        .then((results) => {
          const fresh = results
            .map((result) => result?.body)
            .filter((report): report is Report => Boolean(report));

          // Ни один запрос не дошёл. Раньше здесь стоял голый `return` — без
          // перепланирования таймера и без единого слова человеку, — и одного
          // неудачного тика хватало, чтобы опрос умер навсегда. Отчёт на
          // сервере тем временем собирался и был готов, а карточка вечно
          // показывала «Собираем числа», кнопки «Скачать» не появлялось, и
          // человек жал «Собрать» ещё раз, получая дубль.
          if (fresh.length === 0) {
            misses += 1;
            if (misses > MAX_MISSES) {
              setLost(true);
              return;
            }
            timer = window.setTimeout(
              tick,
              Math.min(POLL_FALLBACK * 2 ** misses, POLL_MAX) * 1000,
            );
            return;
          }

          misses = 0;
          setLost(false);
          const after = results.find((result) => result?.retryAfter)?.retryAfter;
          list.put({
            ...(list.kind === 'ready' ? list.data : { next_cursor: null, limit: 30 }),
            items: (list.kind === 'ready' ? list.data.items : []).map(
              (report) => fresh.find((one) => one.id === report.id) ?? report,
            ),
          });
          const stillBuilding = fresh.some(
            (report) => report.state === 'queued' || report.state === 'building',
          );
          if (stillBuilding)
            timer = window.setTimeout(tick, (after ?? POLL_FALLBACK) * 1000);
        })
        .catch(() => undefined);
    };

    timer = window.setTimeout(tick, POLL_FALLBACK * 1000);
    return () => {
      abort.abort();
      if (timer) window.clearTimeout(timer);
    };
    // Список пересобирается в `put`, и держать его в зависимостях значит
    // перезапускать опрос на каждый успешный ответ.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingIds]);

  // Есть ли по чему собирать отчёт.
  const hasScope = Boolean(scope.brandId || scope.workspaceId);

  const create = () => {
    setBusy(true);
    setFailure(null);
    const to = isoDate(new Date());
    const from = daysAgo(kind === 'monthly' ? 30 : 7);
    api
      .post<Report>(
        '/reports',
        {
          kind,
          format,
          brand_id: scope.brandId ?? null,
          // Пространство передаётся явно, когда бренд не выбран. Без него
          // сервер брал «единственное видимое», а при нескольких отвечал
          // «Укажите пространство для отчёта» — и указать его в этой форме было
          // негде: в ней только «Отчёт», «Формат» и галочка ответов.
          ...(scope.brandId ? {} : { workspace_id: scope.workspaceId ?? null }),
          period_start: from,
          period_end: to,
          basis: 'weekly',
          include_answers: withAnswers,
        },
        { idempotencyKey: idempotencyKey() },
      )
      .then((report) =>
        list.put({
          ...(list.kind === 'ready' ? list.data : { next_cursor: null, limit: 30 }),
          items: [report, ...(list.kind === 'ready' ? list.data.items : [])],
        }),
      )
      .catch(setFailure)
      .finally(() => setBusy(false));
  };

  return (
    <>
      <ScreenHead title="Центр отчётов" />

      {canCreate ? (
        <section className="panel block">
          <h2>Собрать отчёт</h2>
          <div className="filters">
            <div className="field inline">
              <label htmlFor="report-kind">Отчёт</label>
              <select
                id="report-kind"
                value={kind}
                onChange={(event) => setKind(event.target.value as ReportKind)}
              >
                {CREATABLE.map((value) => (
                  <option key={value} value={value}>
                    {reportKind(value)}
                  </option>
                ))}
              </select>
            </div>

            <div className="field inline">
              <label htmlFor="report-format">Формат</label>
              <select
                id="report-format"
                value={format}
                onChange={(event) => setFormat(event.target.value as ReportBuildFormat)}
              >
                {BUILDABLE.map((value) => (
                  <option key={value} value={value}>
                    {FORMAT[value]}
                  </option>
                ))}
              </select>
            </div>

            <label className="chip-filter">
              <input
                type="checkbox"
                checked={withAnswers}
                onChange={(event) => setWithAnswers(event.target.checked)}
              />
              Приложить ответы движков
            </label>

            <button
              className="btn btn-primary btn-sm"
              type="button"
              onClick={create}
              disabled={busy || !hasScope}
            >
              Собрать
            </button>
          </div>
          {hasScope ? null : (
            // Состояние по умолчанию сразу после входа — «Все пространства /
            // Бренд не выбран», и любой щелчок давал отказ «Укажите
            // пространство для отчёта». Человек жал и не понимал, чего от него
            // хотят: ни подсказки, ни блокировки, ни ссылки на переключатель.
            <p className="stamp">
              Отчёт собирается по одному бренду или пространству. {pickBrand()}
            </p>
          )}
        </section>
      ) : null}

      {failure ? <ErrorNotice error={failure} /> : null}

      {list.kind === 'loading' ? (
        <Skeleton rows={3} />
      ) : list.kind === 'failed' ? (
        <ErrorNotice error={list.error} />
      ) : list.data.items.length === 0 ? (
        <Empty eyebrow="Отчёты" title="Ни одного отчёта ещё не собрано">
          Недельные отчёты собираются сами и уходят письмом всем участникам рабочего
          пространства. Здесь же можно собрать разовый — за произвольный период и в нужном
          формате.
        </Empty>
      ) : (
        <>
          <Stamp>
            {countOf(list.data.items.length, 'отчёт', 'отчёта', 'отчётов')} за доступную
            историю{scope.brand ? ` по бренду «${scope.brand.name}»` : ''}.
          </Stamp>
          <div className="items">
            {list.data.items.map((report) => (
              <Row
                key={report.id}
                report={report}
                canShare={canShare}
                canCreate={canCreate}
                lost={lost}
                siblings={list.data.items}
                onCreated={(created) =>
                  list.put({
                    ...list.data,
                    items: [
                      created,
                      ...list.data.items.filter((one) => one.id !== created.id),
                    ],
                  })
                }
                onChange={(updated) =>
                  list.put({
                    ...list.data,
                    items: list.data.items.map((one) =>
                      one.id === updated.id ? updated : one,
                    ),
                  })
                }
              />
            ))}
          </div>
        </>
      )}
    </>
  );
}

function Row({
  report,
  canShare,
  canCreate,
  lost,
  siblings,
  onCreated,
  onChange,
}: {
  report: Report;
  canShare: boolean;
  /** Заказывать сборку вправе не всякая роль — значит, и быстрый экспорт тоже. */
  canCreate: boolean;
  /** Состояние сборки перестало доходить: показываем это вместо вечных шагов. */
  lost: boolean;
  /** Отчёты того же списка: среди них может уже лежать готовый файл нужного формата. */
  siblings: Report[];
  onCreated: (report: Report) => void;
  onChange: (report: Report) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [preparing, setPreparing] = useState<ReportBuildFormat | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  /** Отказ, у которого есть текст для человека: он показывается словами. */
  const [note, setNote] = useState<string | null>(null);
  const abort = useRef<AbortController | null>(null);

  // Ожидание не переживает экран: иначе файл скачается посреди другого раздела.
  useEffect(() => () => abort.current?.abort(), []);

  /**
   * Скачивание отчёта.
   *
   * Здесь стояла `<a href="…/download" download>`, и любой отказ сервера
   * пропадал бесследно: на download-ссылке браузер не показывает ни 503
   * «Хранилище отчётов не настроено», ни 409 «Отчёт ещё не собран», ни 404 —
   * ни сообщения, ни индикатора, ни записи в консоли. Человек жал и решал, что
   * сломался его браузер.
   *
   * Поэтому запрос идёт обычным вызовом: подписанный адрес приходит ответом, и
   * только после успеха браузер уводится на файл. Отказ показывается рядом с
   * кнопкой тем же `ErrorNotice`, что и остальные действия строки.
   */
  const download = () => {
    setBusy(true);
    setFailure(null);
    downloadFile(`/reports/${report.id}/download`)
      .catch(setFailure)
      .finally(() => setBusy(false));
  };

  /**
   * Быстрый экспорт того же периода в другой формат.
   *
   * Кнопка называется «Скачать PDF», и она обязана СКАЧАТЬ — иначе это не
   * кнопка скачивания, а заявка, притворяющаяся кнопкой. Поэтому:
   *
   * 1. если такой файл за этот период уже ЕСТЬ — готовый скачивается сразу, а
   *    собирающийся дожидается. Второй заказ того же не делается: у обычного
   *    отчёта его вернул бы сервер, а у произвольного периода завёл бы вторую
   *    сборку — и человек получил бы два одинаковых файла подряд;
   * 2. если нет — сборка заказывается, и мы ждём её, показывая это словами, а
   *    по готовности уводим браузер на файл.
   *
   * Уход со страницы отменяет всё: без этого ожидание пережило бы экран и
   * скачало файл посреди совсем другого раздела.
   */
  const exportAs = (format: ReportBuildFormat) => {
    const controller = new AbortController();
    abort.current?.abort();
    abort.current = controller;
    setPreparing(format);
    setFailure(null);
    setNote(null);
    orderAndDownload(
      {
        kind: report.kind,
        format,
        brandId: report.brand_id ?? null,
        workspaceId: report.workspace_id,
        periodStart: report.period_start,
        periodEnd: report.period_end,
        siblings,
      },
      controller.signal,
      // Единственное обновление списка отсюда — и оно сразу: дальше состояние
      // строки освежает опрос самой страницы. Держи мы этот обработчик три
      // минуты, он переписал бы список снимком, снятым до клика, стерев чужие
      // правки.
      onCreated,
    )
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        // Причина, написанная для человека, показывается словами; всё
        // остальное — обычным разбором отказа сервера.
        if (error instanceof ExportFailure) setNote(error.message);
        else setFailure(error);
      })
      .finally(() => {
        if (!controller.signal.aborted) setPreparing(null);
      });
  };

  const share = () => {
    setBusy(true);
    setFailure(null);
    api
      .post<components['schemas']['ReportShare']>(
        `/reports/${report.id}/share`,
        { expires_in_days: 7 },
        { idempotencyKey: idempotencyKey() },
      )
      .then((created) => onChange({ ...report, share: created }))
      .catch(setFailure)
      .finally(() => setBusy(false));
  };

  /**
   * Отзыв публичной ссылки спрашивается ДО нажатия.
   *
   * Кнопка красная и срабатывала молча, а ссылка к этому моменту уже разослана
   * заказчику: он открывает её и видит пустоту, о которой никто не предупредил.
   * Тот же кабинет на экране присвоения аудита пишет «мы говорим это до
   * нажатия, а не после» — здесь это не выполнялось.
   */
  const [revoking, setRevoking] = useState(false);

  const revoke = () => {
    setRevoking(false);
    setBusy(true);
    setFailure(null);
    api
      .delete(`/reports/${report.id}/share`)
      .then(() => onChange({ ...report, share: null }))
      .catch(setFailure)
      .finally(() => setBusy(false));
  };

  const building = report.state === 'queued' || report.state === 'building';

  return (
    <article className={`item${report.state === 'failed' ? ' critical' : ''}`}>
      <div className="item-mark">
        <b>{reportKind(report.kind)}</b>
        {FORMAT[report.format]}
        <span className="stamp">{dateNumeric(report.created_at)}</span>
      </div>

      <div className="item-body">
        <h3>{periodFmt(report.period_start, report.period_end)}</h3>

        {building ? (
          lost ? (
            // Шаги без обновления — это ложь: они показывают движение, которого
            // мы уже не видим. Отчёт при этом мог давно собраться.
            <p className="stamp">
              Не можем узнать состояние сборки — обновите страницу. Отчёт при этом
              собирается: повторно запускать сборку не нужно, иначе получится второй такой
              же.
            </p>
          ) : (
            <Pipeline steps={BUILD_STEPS} current={report.state === 'queued' ? 0 : 1} />
          )
        ) : null}

        {report.state === 'failed' ? (
          <p>
            {report.last_error ??
              `Числа в отчёте разошлись с теми, что были на экране, и отчёт не собрался. Соберите заново — если повторится, напишите на ${LEGAL_ENTITY.contact}.`}
          </p>
        ) : null}

        {report.state === 'ready' ? (
          <span className="stamp">
            Готов {report.ready_at ? dateProse(report.ready_at) : ''}
            {report.byte_size ? ` · ${size(report.byte_size)}` : ''}
          </span>
        ) : null}

        {/* Ссылка показывается ТОЛЬКО у готового отчёта. Условие было одно —
            «ссылка выпущена», — а выпущенная ссылка переживает пересборку: отчёт,
            собранный заново и упавший, продолжал рекламировать адрес, который
            отдаёт «Отчёт не найден». Публичная выдача фильтрует по state = 'ready'
            (`insight.resolve_share`), поэтому у неготового отчёта живой ссылки не
            существует в принципе, и печатать её — это обещание, которого продукт
            не выполняет. */}
        {report.share && report.state === 'ready' ? (
          <span className="stamp">
            Публичная ссылка действует до {dateProse(report.share.share_expires_at)}:{' '}
            <a className="linkish" href={report.share.share_url}>
              {report.share.share_url}
            </a>
            . Кто угодно с этой ссылкой увидит числа за период — организацию и соседние
            бренды она не показывает.
          </span>
        ) : null}
        {report.share && report.state === 'failed' ? (
          <span className="stamp">
            Публичная ссылка на этот отчёт не работает, пока он не собран заново: по ней
            открывается «Отчёт не найден».
          </span>
        ) : null}
        {/* Почему у книги и выгрузки ссылки нет, сказано словами. Кнопки тут не
            будет ни серой, ни с подсказкой (app-kit.md §1), но молчание читается
            как поломка: человек публиковал книгу неделю назад и вправе знать,
            куда делась кнопка.
            Условие узкое намеренно. Тому, кто публиковать и так не может, объяснять
            про формат незачем: у него кнопки нет из-за роли, и текст соврал бы о
            причине. У отчёта с ЖИВОЙ ссылкой текст соврал бы дважды: ссылка есть, и
            рядом стоит её отзыв. */}
        {canShare &&
        report.state === 'ready' &&
        report.format !== 'pdf' &&
        !report.share ? (
          <span className="stamp">
            Публичной ссылкой отдаётся только {FORMAT.pdf}. Остальные форматы — рабочие
            файлы: они везут больше, чем показывает публичная страница, книга разрезов —
            вплоть до состава вопросов.
          </span>
        ) : null}

        {preparing ? (
          <p className="stamp" role="status">
            Готовим {FORMAT[preparing]} — файл скачается сам, как только соберётся.
          </p>
        ) : null}

        {note ? (
          <div className="notice" role="alert">
            <b>Не получилось</b>
            {note}
          </div>
        ) : null}

        {failure ? <ErrorNotice error={failure} /> : null}
      </div>

      <div className="item-act">
        {report.state === 'ready' ? (
          <button
            className="btn btn-ink btn-sm"
            type="button"
            onClick={download}
            disabled={busy || preparing !== null}
          >
            {/* Формат этой строки написан слева, в `item-mark`, и повторять его
                в глаголе незачем: «Скачать Страница» — не по-русски, а
                «Скачать страницу» рядом со «Скачать PDF» читается как два
                разных действия над одним файлом. */}
            Скачать
          </button>
        ) : null}

        {report.state === 'ready' && canCreate
          ? QUICK_EXPORTS.filter((format) => format !== report.format).map((format) => (
              <button
                key={format}
                className="btn btn-ghost btn-sm"
                type="button"
                onClick={() => exportAs(format)}
                disabled={busy || preparing !== null}
                aria-busy={preparing === format}
              >
                {preparing === format
                  ? `Готовим ${FORMAT[format]}…`
                  : `Скачать ${FORMAT[format]}`}
              </button>
            ))
          : null}

        {/* Публикуется только PDF: сервис отвечает отказом на остальные форматы
            (`share_report`), и кнопка, которая всегда получает отказ, — это
            дефект интерфейса. Отзыв остаётся у любого формата: ссылки, выданные
            до этого правила, гасить чем-то надо. */}
        {canShare &&
        report.state === 'ready' &&
        (report.share || report.format === 'pdf') ? (
          report.share ? (
            <button
              className="btn btn-danger btn-sm"
              type="button"
              onClick={() => setRevoking(true)}
              disabled={busy}
            >
              Отозвать ссылку
            </button>
          ) : (
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              onClick={share}
              disabled={busy}
            >
              Публичная ссылка
            </button>
          )
        ) : null}
      </div>

      <Confirm
        open={revoking}
        title="Отозвать публичную ссылку"
        action="Отозвать ссылку"
        danger
        onConfirm={revoke}
        onCancel={() => setRevoking(false)}
      >
        Ссылка перестанет открываться у всех, кому вы её отправили. Сам отчёт останется в
        кабинете, и новую ссылку можно выпустить в любой момент.
      </Confirm>
    </article>
  );
}

/** Размер файла: до мегабайта — в килобайтах, дальше — с десятой долей. */
function size(bytes: number): string {
  const kb = bytes / 1024;
  return kb < 1024 ? `${int(Math.round(kb))} КБ` : `${decimal(kb / 1024, 1)} МБ`;
}
